import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import ComplianceWall from "@/components/home/ComplianceWall";
import MagneticButton from "@/components/site/MagneticButton";
import { getTeam } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const TIMELINE = [
  { year: "2021", event: "RockLadder founded to build a customer-centric, employee-friendly cyber-security & analytics company." },
  { year: "2022", event: "First tier-1 BFSI MSSP engagement live. SOC operational across three shifts." },
  { year: "2023", event: "Expanded into OT/ICS security for national energy operators. IEC 62443 practice launched." },
  { year: "2024", event: "MDR/XDR pod model rolled out. 500B+ events/day analysed across the client base." },
  { year: "2025", event: "Threat intelligence + digital risk protection service launched. STIX/TAXII feeds live." },
  { year: "2026", event: "AI-augmented SOC and offensive AI-red-team practice go GA. Global follow-the-sun coverage." },
];

const VISION = "To be the most admired global company by being customer-centric and employee-friendly.";
const MISSION = "To be one of the world's leading Cyber Security & Analytics companies — leveraging world-class talent to build a portfolio of brands and to differentiate our offerings with creative, innovative, cutting-edge and customer-centric solutions.";

export default function About() {
  const [leadership, setLeadership] = useState([]);
  const [advisory, setAdvisory] = useState([]);

  useEffect(() => {
    getTeam({ category: "leadership" }).then(setLeadership).catch(() => {});
    getTeam({ category: "advisory" }).then(setAdvisory).catch(() => {});
  }, []);

  useSEO({
    title: "About RockLadder — Elevating Cyber Resilience",
    description:
      "Our story, our people, and the operating principles that let us sign SLAs others hedge on.",
    canonical: "https://rockladder.com/about",
  });

  return (
    <>
      <PageHero
        eyebrow="OUR STORY"
        title="Built by operators."
        gradient="For operators."
        subtitle="RockLadder Technologies was founded on a simple idea — security decisions should be made by people who've shipped programs. Not by people who've drawn slides about them."
        breadcrumb={[{ label: "About" }]}
      />

      {/* Story */}
      <section className="relative py-16">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="glass-panel rounded-2xl p-8">
            <div className="font-mono text-[10px] tracking-[0.28em] text-gold-body mb-3">VISION</div>
            <p className="text-lg md:text-xl text-inkText-primary/90 leading-relaxed">
              {VISION}
            </p>
          </div>
          <div className="glass-panel rounded-2xl p-8">
            <div className="font-mono text-[10px] tracking-[0.28em] text-gold-body mb-3">MISSION</div>
            <p className="text-[15px] md:text-base text-inkText-primary/90 leading-relaxed">
              {MISSION}
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-16 md:py-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-10">
            Milestones
          </h2>
          <div className="relative border-l border-gold/25 pl-6 md:pl-10 space-y-8">
            {TIMELINE.map((t, i) => (
              <motion.div
                key={t.year}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="relative"
                data-testid={`timeline-${t.year}`}
              >
                <span className="absolute -left-[33px] md:-left-[45px] top-1 h-3 w-3 rounded-full bg-gradient-to-br from-gold to-violet2 shadow-[0_0_12px_-2px_rgba(245,184,65,0.7)]" />
                <div className="font-mono text-[11px] tracking-[0.24em] text-gold-body">{t.year}</div>
                <p className="mt-1 text-sm md:text-base text-inkText-muted leading-relaxed max-w-2xl">
                  {t.event}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      {leadership.length > 0 && (
        <section className="relative py-16">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-10">
              Leadership
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {leadership.map((m) => (
                <div key={m.name} className="glass-panel rounded-2xl p-5" data-testid={`team-${m.name.split(" ")[0].toLowerCase()}`}>
                  <div
                    className="w-full aspect-square rounded-xl mb-4 border border-gold/20"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(245,184,65,0.18) 0%, rgba(11,15,30,0.6) 40%, rgba(139,92,246,0.22) 100%)",
                    }}
                  />
                  <div className="font-display text-base text-inkText-primary">{m.name}</div>
                  <div className="font-mono text-[10px] tracking-[0.22em] text-gold-body mt-1">
                    {m.role.toUpperCase()}
                  </div>
                  <p className="mt-3 text-xs text-inkText-muted leading-relaxed line-clamp-4">
                    {m.bio}
                  </p>
                  {m.linkedin && (
                    <a
                      href={m.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-1.5 text-inkText-muted hover:text-gold text-xs"
                    >
                      <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Advisory */}
      {advisory.length > 0 && (
        <section className="relative py-16">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-10">
              Advisory Board
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {advisory.map((m) => (
                <div key={m.name} className="glass-panel rounded-2xl p-6 flex gap-5">
                  <div
                    className="w-20 h-20 md:w-24 md:h-24 rounded-xl border border-gold/20 flex-shrink-0"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(245,184,65,0.18) 0%, rgba(11,15,30,0.6) 40%, rgba(139,92,246,0.22) 100%)",
                    }}
                  />
                  <div>
                    <div className="font-display text-base text-inkText-primary">{m.name}</div>
                    <div className="font-mono text-[10px] tracking-[0.22em] text-gold-body mt-1">
                      {m.role.toUpperCase()}
                    </div>
                    <p className="mt-2 text-xs text-inkText-muted leading-relaxed">{m.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <ComplianceWall />

      <section className="relative pb-24 pt-8">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="glass-panel-strong rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="font-display text-xl md:text-2xl text-inkText-primary">
                Interested in joining us?
              </div>
              <p className="text-sm text-inkText-muted mt-1">
                We hire operators who&apos;ve shipped detections that mattered.
              </p>
            </div>
            <MagneticButton as={Link} to="/careers" variant="primary">
              See open roles
            </MagneticButton>
          </div>
        </div>
      </section>
    </>
  );
}
